import React, { useState } from 'react';

const ACTION_BUTTONS = [
  { id: 'upload', label: 'Upload', icon: '📁' },
  { id: 'undo', label: 'Undo', icon: '↩️' },
  { id: 'redo', label: 'Redo', icon: '↪️' },
  { id: 'revert', label: 'Revert', icon: '🔄' },
];

const TOOL_GROUPS = [
  // Basic operations
  [
    { id: 'crop', label: 'Crop', icon: '✂️' },
    { id: 'resize', label: 'Resize', icon: '↔️' },
    { id: 'flip', label: 'Flip', icon: '🔄' },
  ],
  // Enhancement
  [
    { id: 'backgroundRemove', label: 'BG Remove', icon: '🖼️' },
    { id: 'penTool', label: 'Pen Tool', icon: '✒️' },
    { id: 'skinEnhance', label: 'Skin', icon: '✨' },
  ],
  // Color & adjustments
  [
    { id: 'colorBalance', label: 'Color', icon: '🎨' },
    { id: 'curves', label: 'Curves', icon: '〰️' },
    { id: 'brightnessContrast', label: 'Bright', icon: '☀️' },
    { id: 'sharpen', label: 'Sharpen', icon: '🔍' },
    { id: 'vignette', label: 'Vignette', icon: '⭕' },
  ],
  // Fix
  [
    { id: 'redEye', label: 'Red Eye', icon: '👁️' },
    { id: 'blemish', label: 'Blemish', icon: '🩹' },
  ],
  // Actions
  [
    { id: 'save', label: 'Save', icon: '💾' },
    { id: 'download', label: 'Download', icon: '⬇️' },
  ],
];

const toolbarStyle = {
  width: 88,
  background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '10px 0',
  gap: 2,
  overflowY: 'auto',
  borderRight: '1px solid rgba(102,126,234,0.1)',
  flexShrink: 0,
};

const btnBase = {
  width: 64,
  height: 56,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  color: '#888',
  fontSize: 10,
  gap: 3,
  padding: 4,
  transition: 'all 0.2s',
};

const btnActive = {
  ...btnBase,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
};

const actionBtn = {
  ...btnBase,
  height: 48,
  color: '#aaa',
};

const separatorStyle = {
  width: 48,
  height: 1,
  background: 'rgba(102,126,234,0.15)',
  margin: '6px 0',
  flexShrink: 0,
};

/**
 * Toolbar — vertical sidebar with action buttons at top and tool groups separated by dividers.
 */
export default function Toolbar({
  activeTool,
  onSelectTool,
  onUpload,
  onUndo,
  onRedo,
  onRevert,
  canUndo,
  canRedo,
  hasOriginal,
}) {
  const [hoveredId, setHoveredId] = useState(null);

  const getHoverStyle = (id) => {
    if (hoveredId === id && activeTool !== id) {
      return { background: 'rgba(102,126,234,0.12)', color: '#ccc' };
    }
    return {};
  };

  const actionHandlers = {
    upload: onUpload,
    undo: onUndo,
    redo: onRedo,
    revert: onRevert,
  };

  const actionDisabled = {
    undo: !canUndo,
    redo: !canRedo,
    revert: !hasOriginal,
  };

  return (
    <div style={toolbarStyle}>
      {/* Action buttons at top */}
      {ACTION_BUTTONS.map((btn) => (
        <button
          key={btn.id}
          title={btn.label}
          disabled={actionDisabled[btn.id]}
          style={{
            ...actionBtn,
            opacity: actionDisabled[btn.id] ? 0.35 : 1,
            cursor: actionDisabled[btn.id] ? 'default' : 'pointer',
            ...getHoverStyle(btn.id),
          }}
          onClick={actionHandlers[btn.id]}
          onMouseEnter={() => setHoveredId(btn.id)}
          onMouseLeave={() => setHoveredId(null)}
        >
          <span style={{ fontSize: 20 }}>{btn.icon}</span>
          <span>{btn.label}</span>
        </button>
      ))}

      <div style={separatorStyle} />

      {/* Tool groups */}
      {TOOL_GROUPS.map((group, gi) => (
        <React.Fragment key={gi}>
          {group.map((tool) => (
            <button
              key={tool.id}
              title={tool.label}
              style={{
                ...(activeTool === tool.id ? btnActive : btnBase),
                ...getHoverStyle(tool.id),
              }}
              onClick={() => onSelectTool(tool.id === activeTool ? null : tool.id)}
              onMouseEnter={() => setHoveredId(tool.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <span style={{ fontSize: 20 }}>{tool.icon}</span>
              <span>{tool.label}</span>
            </button>
          ))}
          {gi < TOOL_GROUPS.length - 1 && <div style={separatorStyle} />}
        </React.Fragment>
      ))}
    </div>
  );
}
